import { type IResource, useResource } from '@landsbankinn/l16n';
import { Form } from '@landsbankinn/ui-kit';
import { type FormEvent, type ReactNode, useRef } from 'react';
import { FadeIn } from 'src/components/Layout/FadeIn';
import { useApplicationClientContext } from '../../../state';
import { type FormField, type FormType } from '../../../state/server';
import { loginRes } from '../Login.res';
import { submitAccessDenied } from '../utils';

export enum HiddenFieldName {
  xsrf = 'xsrf',
  traceparent = 'traceparent',
}

export type HiddenFieldValueProvider = (
  name: HiddenFieldName,
) => string | undefined;

function createHiddenFields(
  form: FormType,
  fieldValueProvider?: HiddenFieldValueProvider,
): ReactNode[] {
  const createHiddenInput = (field: FormField, name: HiddenFieldName) => {
    // Value is either from the original INITIAL_STATE in form.fields['fieldname'].value or
    // given by the custom fieldValueProvider function that parent component can defined.
    // This was done to support more complex auth flows like Audkenni App that requires an
    // intermediate request that return custom code and a new XSRF token for the actual form post.
    const value = fieldValueProvider
      ? fieldValueProvider(name) || field.value
      : field.value;
    return (
      <input key={field.name} type="hidden" name={field.name} value={value} />
    );
  };
  const hiddenFields: ReactNode[] = [
    HiddenFieldName.traceparent,
    HiddenFieldName.xsrf,
  ].map((o) => createHiddenInput(form.fields[o], o));

  return hiddenFields;
}

function getAriaLabel(
  action: string,
  r: (resources: IResource, count?: number | undefined) => string,
) {
  switch (action) {
    case '/ui/login/audkenniapp':
      return r(loginRes.auth_audkenniApp);
    case '/ui/login/password':
      return r(loginRes.auth_password);
    case '/ui/login/pkisim':
    default:
      return r(loginRes.auth_pkiSim);
  }
}
export type LoginFormProps = {
  form: FormType;
  hidden?: boolean;
  onSubmit: (e: FormEvent<HTMLFormElement>) => void;
  children?: ReactNode;
  fieldValueProvider?: HiddenFieldValueProvider;
};
export function LoginForm(props: LoginFormProps) {
  const { r } = useResource();
  const hiddenFields = createHiddenFields(props.form, props.fieldValueProvider);
  return (
    <FadeIn grow="1">
      <Form
        grow="1"
        aria-label={getAriaLabel(props.form.action, r)}
        action={props.form.action}
        method="post"
        instrumentAs="login_form"
        onSubmit={props.onSubmit}
        hidden={props.hidden ?? false}
      >
        {hiddenFields}
        {props.children}
      </Form>
    </FadeIn>
  );
}

export function CancelForm(props: {
  form: FormType;
  children: (submitCancel: () => void) => ReactNode;
}) {
  const [, dispatch] = useApplicationClientContext();

  const hiddenFields = createHiddenFields(props.form);
  const formRef = useRef<HTMLFormElement>(null);

  const children = props.children(() => {
    submitAccessDenied(dispatch, formRef?.current ?? undefined);
  });

  return (
    <Form
      ref={formRef}
      action={props.form.action}
      method="post"
      onSubmit={() => {}}
    >
      {hiddenFields}
      {children}
    </Form>
  );
}
